import React, { Suspense } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { StyledOutboundLink, CtaLink } from 'gatsby-theme-pdv/src/components/cta';
import AddToCalendar from './add-to-calendar';

const salesSeminarLink = '/sales-seminar/';

const formatDay = (day) => {
  switch (day) {
    case 1:
      return 'maandag';
    case 2:
      return 'dinsdag';
    case 3:
      return 'woensdag';
    case 4:
      return 'donderdag';
    case 5:
      return 'vrijdag';
    case 6:
      return 'zaterdag';
    case 7:
      return 'zondag';
    default:
      return null;
  }
};

// add Event fragment, to be used in events template
export const query = graphql`
  fragment Event on WpAgenda {
    title
    dateDay
    dateDayAndMonth
    agendaAcf {
      agendaDate
      location
      buttonText
      buttonLink
      endTime
      startTime
    }
  }

  fragment PastEvent on WpAgenda {
    ...Event
    dateYear
  }
`;

const Event = ({
  hasEnded = false,
  content = null,
  title,
  agendaAcf,
  dateDay,
  dateDayAndMonth,
  dateYear,
}) => (
  <Wrapper>
    <div>
      <Day>{formatDay(dateDay)}</Day>
      <Date>{dateDayAndMonth}</Date>
      {dateYear && (
        <Year>{dateYear}</Year>
      )}
    </div>

    <div>
      <Title dangerouslySetInnerHTML={{ __html: title }} />
      <SubTitle>{agendaAcf.location}</SubTitle>
    </div>

    {!hasEnded && (
      <Ctas>
        <div>
          {agendaAcf.buttonText && agendaAcf.buttonLink && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {agendaAcf.buttonLink === salesSeminarLink ? (
                <CtaLink to={agendaAcf.buttonLink} variant="outline-primary">
                  {agendaAcf.buttonText}
                </CtaLink>
              ) : (
                <StyledOutboundLink href={agendaAcf.buttonLink} variant="outline-primary">
                  {agendaAcf.buttonText}
                </StyledOutboundLink>
              )}
            </>
          )}
        </div>

        {agendaAcf.startTime && (
          <Suspense fallback={null}>
            <AddToCalendar
              date={agendaAcf.agendaDate}
              startTime={agendaAcf.startTime}
              endTime={agendaAcf.endTime || agendaAcf.startTime}
              title={title}
              description={content}
              location={agendaAcf.location}
            />
          </Suspense>
        )}
      </Ctas>
    )}
  </Wrapper>
);

const Wrapper = styled.div`
  border-top: 1px solid ${(props) => props.theme.gray2};
  padding: 30px 0;

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    display: grid;
    grid-auto-rows: auto;
    grid-template-columns: 1fr 6fr 2fr;
    grid-gap: ${(props) => props.theme.grid.gap};
    align-items: center;
  }

  &:last-child {
    border-bottom: 1px solid ${(props) => props.theme.gray2};
  }
`;

const Day = styled.span`
  color: ${(props) => props.theme.gray3};
  font-size:  1rem;
  text-transform: uppercase;
  padding-right: 0.2rem;

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    display: block;
    padding-right: 0;
    font-size:  0.8889rem;
  }
`;

const Date = styled.span`
  font-size:  1rem;
  color: ${(props) => props.theme.gray3};

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    font-family: ${(props) => props.theme.standoutFontFamily};
    font-size: 1.556rem;
    font-weight: 700;
    color: inherit;
  }
`;

const Year = styled.span`
  color: ${(props) => props.theme.gray3};
  font-size:  1rem;
  text-transform: uppercase;
  padding-right: 0.2rem;

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    display: block;
    padding-right: 0;
    font-size:  0.8889rem;
  }
`;

const Title = styled.h3`
  font-family: ${(props) => props.theme.standoutFontFamily};
  margin: 6px 0;
  font-size: 1rem;

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    margin: 0 0 6px 0;
  }
`;

const SubTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size:  0.8889rem;
  color: ${(props) => props.theme.gray3};
  font-weight: normal;

  @media all and (min-width: ${(props) => props.theme.breakingpoints.lg}) {
    margin: 0;
  }
`;

const Ctas = styled.div`
  display: flex;
  justify-content: space-between;

  ${StyledOutboundLink} {
    flex-grow: 1;
  }
`;

export default Event;
